import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import {
  REASON, termsOf, splitHits, hasAny, fullDate, minutesLabel, clock, turnsOf, guestName,
  overviewPoints, noteSections, actionGroups, ourNames,
} from "./callsModel.mjs";

/*
 * ONE CALL (Meetings ▸ Calls): Fireflies' notes on the left, the transcript on
 * the right when the pane is wide enough, one under the other when it is not.
 * The auditor's call page (Pulse → Clients) is the pattern, asked for by name on
 * 2026-10-22; the write-up here is Fireflies' own, since the CRM writes none.
 *
 * The transcript is read from Fireflies when the call opens (GET
 * /calls/:id/transcript), never stored. Every timestamp opens the call in
 * Fireflies at that moment. A guest who is a contact opens their record.
 *
 * Props:
 *   callId    the Fireflies id
 *   search    the page's search, pre-filled into the transcript's find box
 *   onFiled   () => void, after the call is filed under another company
 */

export function Hl({ text, terms }) {
  return splitHits(text, terms).map((p, i) => (p.hit ? <mark key={i} className="cl-hit">{p.text}</mark> : <React.Fragment key={i}>{p.text}</React.Fragment>));
}

export function useCall(callId) {
  const [state, setState] = useState({ status: "idle" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!callId) return undefined;
    let alive = true;
    setState((s) => (s.data && s.data.call.id === callId ? s : { status: "loading" }));
    api(`/calls/${encodeURIComponent(callId)}`)
      .then((data) => alive && setState({ status: "ok", data }))
      .catch((e) => alive && setState({ status: "error", error: e.status === 404 ? "This call isn't in the log." : e.message }));
    return () => {
      alive = false;
    };
  }, [callId, n]);
  return { ...state, reload: () => setN((x) => x + 1) };
}

export default function CallView({ callId, search = "", onFiled }) {
  const { status, data, error, reload } = useCall(callId);
  if (!callId) return <div className="cl-empty">Pick a call.</div>;
  if (status === "error") return <div className="cl-empty">{error}</div>;
  if (!data) return <div className="cl-empty">Loading…</div>;
  return <CallArticle key={data.call.id} data={data} search={search} onFiled={() => { reload(); if (onFiled) onFiled(); }} />;
}

function CallArticle({ data, search, onFiled }) {
  const { call, other_calls: others } = data;
  const terms = useMemo(() => termsOf(search), [search]);
  const names = useMemo(() => ourNames(call.attendees), [call.attendees]);
  const notes = call.notes || {};
  const points = overviewPoints(notes.overview);
  const sections = noteSections(notes.shorthand_bullet);
  const actions = actionGroups(notes.action_items, names);
  const theirs = call.attendees.filter((a) => !a.ours);
  const ours = call.attendees.filter((a) => a.ours);
  const recordOf = call.attendees.find((a) => a.contact_id && !a.ours);
  const at = (s) => `${call.fireflies_url}?t=${Math.floor(s)}`;
  const [showNotes, setShowNotes] = useState(false);

  return (
    <article className="cl-article">
      <div className="cl-writeup" dir="auto">
        <div className="cl-kicker">
          {call.company_name ? (
            recordOf ? <Link to={`/contacts/${recordOf.contact_id}`} className="cl-company">{call.company_name}</Link> : <span className="cl-company">{call.company_name}</span>
          ) : (
            <span className="cl-company cl-company--none">{call.match_reason === "internal" ? "Internal" : "Not filed"}</span>
          )}
          {call.company_status && <span className={`cl-status cl-status--${call.company_status}`}>{call.company_status.replace(/_/g, " ")}</span>}
          <span className="cl-reason">{REASON[call.match_reason] || ""}</span>
          <FileUnder call={call} onFiled={onFiled} />
        </div>
        <h1 className="cl-title"><Hl text={call.title} terms={terms} /></h1>
        <div className="cl-meta">
          {[fullDate(call.started_at), minutesLabel(call.duration_min)].filter(Boolean).join(" · ")}
          {" · "}
          <a href={call.fireflies_url} target="_blank" rel="noopener noreferrer">Open in Fireflies ↗</a>
        </div>
        {call.also_with.length > 0 && (
          <div className="cl-meta">Also on {call.also_with.map((c) => c.name).join(", ")}'s record</div>
        )}

        <div className="cl-people">
          {theirs.length > 0 && (
            <div className="cl-people__row">
              <span className="cl-people__label">{call.company_name || "Guests"}</span>
              {theirs.map((a) => <Guest key={a.email || a.name} a={a} />)}
            </div>
          )}
          {ours.length > 0 && (
            <div className="cl-people__row">
              <span className="cl-people__label">Remotion</span>
              {ours.map((a) => <Guest key={a.email || a.name} a={a} />)}
            </div>
          )}
        </div>

        {!call.notes && (
          <div className="cl-callout">
            {call.notes_status && call.notes_status !== "processed" && call.notes_status !== "skipped"
              ? "Fireflies is still writing its notes on this call. They show here within 15 minutes of being ready."
              : "Fireflies wrote no notes for this call."}
          </div>
        )}

        {(notes.short_summary || (!points.length && notes.overview)) && (
          <p className="cl-summary"><Hl text={notes.short_summary || notes.overview} terms={terms} /></p>
        )}

        {points.length > 0 && (
          <section className="cl-section">
            <h2>Key points</h2>
            <ul className="cl-points">
              {points.map((p, i) => (
                <li key={i}>{p.label && <b>{p.label}: </b>}<Hl text={p.text} terms={terms} /></li>
              ))}
            </ul>
          </section>
        )}

        {actions.length > 0 && (
          <section className="cl-section">
            <h2>Action items</h2>
            <div className="cl-actions">
              {actions.map((g) => (
                <div key={g.owner} className="cl-owner">
                  <div className="cl-owner__name">
                    {g.owner}
                    <span className={`cl-side${g.ours ? " cl-side--ours" : ""}`}>{g.ours ? "Ours" : "Theirs"}</span>
                  </div>
                  <ul className="cl-points">
                    {g.items.map((it, i) => (
                      <li key={i}>
                        <Hl text={it.text} terms={terms} />
                        {it.at !== null && <a className="cl-clock cl-clock--inline" href={at(it.at)} target="_blank" rel="noopener noreferrer">{clock(it.at)}</a>}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}

        {sections.length > 0 && (
          <section className="cl-section">
            <h2>
              <button type="button" className="cl-toggle" aria-expanded={showNotes} onClick={() => setShowNotes((v) => !v)}>
                {showNotes ? "▾" : "▸"} Notes, topic by topic · {sections.length}
              </button>
            </h2>
            {showNotes && sections.map((s, i) => (
              <div key={i} className="cl-topic">
                {s.title && (
                  <div className="cl-topic__head">
                    {s.icon && <span aria-hidden="true">{s.icon} </span>}
                    <b>{s.title}</b>
                    {s.range && <a className="cl-clock" href={at(s.from)} target="_blank" rel="noopener noreferrer">{s.range}</a>}
                  </div>
                )}
                <ul className="cl-points">
                  {s.lines.map((l, j) => <li key={j}><Hl text={l} terms={terms} /></li>)}
                </ul>
              </div>
            ))}
          </section>
        )}

        {notes.keywords && notes.keywords.length > 0 && (
          <div className="cl-keywords">
            {notes.keywords.map((k) => <span key={k} className="cl-keyword">{k}</span>)}
          </div>
        )}

        {others.length > 0 && (
          <section className="cl-section">
            <h2>Other calls with {call.company_name}</h2>
            <ul className="cl-others">
              {others.map((o) => (
                <li key={o.id}>
                  <Link to={`/meetings/calls/${encodeURIComponent(o.id)}`}>
                    <span className="cl-others__date">{fullDate(o.started_at).split(" · ")[0]}</span>
                    <span className="cl-others__title">{o.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      <section className="cl-section cl-txsection">
        <h2>Transcript</h2>
        <Transcript callId={call.id} firefliesUrl={call.fireflies_url} initialFind={search} ours={names} />
      </section>
    </article>
  );
}

function Guest({ a }) {
  const label = guestName(a);
  const cls = `cl-chip${a.ours ? " cl-chip--ours" : ""}`;
  if (a.contact_id && !a.ours) {
    return <Link to={`/contacts/${a.contact_id}`} className={`${cls} cl-chip--link`} title={a.email || undefined}>{label}</Link>;
  }
  return <span className={cls} title={a.email || undefined}>{label}</span>;
}

/*
 * File it somewhere else: a company search, or "no company". Filed by hand, the
 * call stays where it is put; the sync never refiles it.
 */
function FileUnder({ call, onFiled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [found, setFound] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!open || q.trim().length < 2) {
      setFound([]);
      return undefined;
    }
    let alive = true;
    const t = setTimeout(() => {
      api(`/companies?q=${encodeURIComponent(q.trim())}&limit=8`)
        .then((d) => alive && setFound(d.companies || []))
        .catch(() => alive && setFound([]));
    }, 200);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q, open]);
  if (!open) {
    return (
      <button type="button" className="cl-link" onClick={() => setOpen(true)}>
        {call.company_id ? "Wrong company?" : "File under a company"}
      </button>
    );
  }
  const file = async (companyId) => {
    setBusy(true);
    setErr(null);
    try {
      await api(`/calls/${encodeURIComponent(call.id)}/company`, { method: "PUT", body: JSON.stringify({ company_id: companyId }) });
      setOpen(false);
      setQ("");
      onFiled();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="cl-file">
      <input
        className="cl-input" autoFocus placeholder="Find a company" value={q} spellCheck={false}
        onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
        aria-label="Find a company"
      />
      {found.length > 0 && (
        <ul className="cl-file__list">
          {found.map((c) => (
            <li key={c.id}>
              <button type="button" disabled={busy} onClick={() => file(c.id)}>
                {c.name}{c.domain ? <span className="cl-muted"> · {c.domain}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="cl-file__foot">
        {call.company_id && <button type="button" className="cl-link" disabled={busy} onClick={() => file(null)}>Not a company's call</button>}
        <button type="button" className="cl-link" onClick={() => setOpen(false)}>Cancel</button>
        {err && <span className="cl-err">{err}</span>}
      </div>
    </div>
  );
}

/*
 * The transcript, read from Fireflies when the call opens. The find box (filled
 * with the page's search) counts the turns that mention it and steps through
 * them with Enter / Shift+Enter; matches are highlighted. Our side's turns are
 * marked, so his own words are easy to skip past or find.
 */
function Transcript({ callId, firefliesUrl, initialFind, ours }) {
  const [state, setState] = useState({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const [find, setFind] = useState(initialFind);
  const [at, setAt] = useState(0);
  const boxRef = useRef(null);
  useEffect(() => {
    setFind(initialFind);
  }, [initialFind]);
  useEffect(() => {
    let alive = true;
    setState({ status: "loading" });
    api(`/calls/${encodeURIComponent(callId)}/transcript`)
      .then((d) => alive && setState({ status: "ok", turns: turnsOf(d.sentences) }))
      .catch((e) => alive && setState({ status: "error", error: e.message }));
    return () => {
      alive = false;
    };
  }, [callId, attempt]);
  const terms = useMemo(() => termsOf(find), [find]);
  const turns = state.status === "ok" ? state.turns : [];
  const hits = useMemo(
    () => (terms.length ? turns.map((t, i) => (hasAny(`${t.speaker || ""} ${t.text}`, terms) ? i : -1)).filter((i) => i >= 0) : []),
    [turns, terms]
  );
  useEffect(() => setAt(0), [find]);
  const cur = hits.length ? hits[Math.min(at, hits.length - 1)] : -1;
  useEffect(() => {
    if (cur < 0 || !boxRef.current) return;
    const el = boxRef.current.querySelector(`[data-turn="${cur}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [cur]);

  if (state.status === "loading") return <div className="cl-muted">Reading the transcript from Fireflies…</div>;
  if (state.status === "error") {
    return (
      <div className="cl-muted">
        Couldn't read it: {state.error}{" "}
        <button type="button" className="cl-link" onClick={() => setAttempt((a) => a + 1)}>Try again</button>
      </div>
    );
  }
  if (!turns.length) return <div className="cl-muted">Fireflies has no transcript for this call.</div>;
  const step = (d) => hits.length && setAt((a) => (a + d + hits.length) % hits.length);
  const isOurs = (speaker) => {
    const words = String(speaker || "").toLowerCase().split(/\s+/);
    return ours.some((n) => n !== "remotion" && words.includes(n));
  };
  return (
    <>
      <div className="cl-find">
        <input
          className="cl-input" value={find} onChange={(e) => setFind(e.target.value)} placeholder="Find in the transcript"
          aria-label="Find in the transcript" spellCheck={false}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              step(e.shiftKey ? -1 : 1);
            }
          }}
        />
        <span className="cl-find__count">
          {terms.length ? (hits.length ? `${Math.min(at, hits.length - 1) + 1} of ${hits.length}` : "No match") : `${turns.length} turns`}
        </span>
        <button type="button" className="cl-find__btn" onClick={() => step(-1)} disabled={!hits.length} aria-label="Previous match">↑</button>
        <button type="button" className="cl-find__btn" onClick={() => step(1)} disabled={!hits.length} aria-label="Next match">↓</button>
      </div>
      <div className="cl-transcript" ref={boxRef} dir="auto">
        {turns.map((t, i) => (
          <p key={i} data-turn={i} className={`${i === cur ? "cl-turn--cur " : ""}${isOurs(t.speaker) ? "cl-turn--ours" : ""}`} dir="auto">
            <a className="cl-clock" href={`${firefliesUrl}?t=${Math.floor(t.at)}`} target="_blank" rel="noopener noreferrer">{clock(t.at)}</a>
            <strong><Hl text={t.speaker || "Speaker"} terms={terms} /></strong> <Hl text={t.text} terms={terms} />
          </p>
        ))}
      </div>
    </>
  );
}
